import type { MessageAttachmentDto } from "@/api/messaging";
import { brokersApi } from "@/api/brokers";
import { brokeragesApi } from "@/api/brokerages";
import { dealsApi } from "@/api/deals";
import { leadsApi } from "@/api/leads";
import { personsApi, entitiesApi, businessesApi } from "@/api/counterparties";
import { vendorsApi, loansApi } from "@/api/portfolioAdmin/client";
import { localEnforcersApi } from "@/api/portfolioAdmin/pr4bClient";
import { tpsApi } from "@/api/portfolioAdmin/tps";
import { spvTrustsApi } from "@/api/spvTrusts";
import { spvPoolsApi } from "@/api/spvPools";
import { investorsApi } from "@/api/investors";
import {
  indentureTrusteesApi,
  trustCompaniesApi,
  placementAgentsApi,
  backupServicersApi,
} from "@/api/serviceProviders";
import { reunderwritingCasesApi } from "@/api/reunderwriting";
import { portalAccountsApi } from "@/api/portalAccounts";
import { partiesApi } from "@/api/parties";
import { buyersApi, listingsApi, buyerSubmissionsApi } from "@/api/buyers";
import type { PartyType } from "@/types/parties";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import {
  listingShortName,
  listingShortNameOrFallback,
  shortenListingName,
} from "@/lib/listingDisplay";

/**
 * Internal Messaging Integration Standard v1.1 §6.3 — registry of
 * resource-pickers used by the composer's Attach modal. Each entry adapts
 * the entity module's existing list endpoint into the row shape the
 * picker UI needs. Adding a new entity type is one entry here + a server-
 * side label resolver (§6.2); no new picker components are introduced
 * because the modules already have search-by-q list APIs.
 *
 * The keys MUST mirror the lowercase singular ResourceTypes constants from
 * the messaging service (Phoenix.Capital.Core/Constants/Messaging/
 * ResourceTypes.cs). Types whose modules have not shipped (loan, vendor,
 * spv, investor, candidate) are intentionally absent — the modal filters
 * the type dropdown to entries that exist here AND in
 * <c>messagesApi.resourceTypes()</c>.
 */

export interface ResourcePickerRow {
  id: string;
  primary: string;
  secondary?: string | null;
  /** Optimistic chip the composer drafts on select. Server re-resolves on send. */
  toAttachment(): MessageAttachmentDto;
}

export interface ResourcePickerAdapter {
  /** Type label shown in the modal's dropdown. */
  typeLabel: string;
  /** Placeholder shown in the search input for this type. */
  placeholder: string;
  /** Server-side search; returns up to ~20 rows. */
  search(q: string): Promise<ResourcePickerRow[]>;
}

const PAGE_SIZE = 20;

/** Borrower/Seller party adapters share one factory (same list API,
 *  parameterized by PartyType; tokens + routes stay per-type). */
function buildPartyPicker(type: PartyType): ResourcePickerAdapter {
  const token = type === "Borrower" ? "borrower" : "seller";
  const basePath = type === "Borrower" ? "/borrowers" : "/sellers";
  return {
    typeLabel: type,
    placeholder: `Search ${type.toLowerCase()}s by name or email…`,
    async search(q) {
      const page = await partiesApi.list(type, { search: q, limit: PAGE_SIZE });
      return page.data.map<ResourcePickerRow>((p) => ({
        id: p.id,
        primary: p.displayName,
        secondary: [p.email, [p.city, p.state].filter(Boolean).join(", ")]
          .filter(Boolean)
          .join(" • "),
        toAttachment: () => ({
          resourceType: token,
          resourceId: p.id,
          displayLabel: `${type} — ${p.displayName}`,
          detailUrl: `${basePath}/${p.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        }),
      }));
    },
  };
}

const borrower = buildPartyPicker("Borrower");
const sellerParty = buildPartyPicker("Seller");

const broker: ResourcePickerAdapter = {
  typeLabel: "Broker",
  placeholder: "Search brokers by company or contact…",
  async search(q) {
    const page = await brokersApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((b) => ({
      id: b.id,
      primary: brokerPickerLabel(b).primary,
      secondary: [b.name, b.email, b.status, b.state]
        .filter(Boolean)
        .join(" • "),
      toAttachment: () => ({
        resourceType: "broker",
        resourceId: b.id,
        displayLabel: b.dba
          ? `Broker — ${b.companyName} (${b.dba})`
          : `Broker — ${b.companyName}`,
        detailUrl: `/brokers/${b.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const brokerage: ResourcePickerAdapter = {
  typeLabel: "Brokerage",
  placeholder: "Search brokerage firms by name…",
  async search(q) {
    const rows = await brokeragesApi.search(q, PAGE_SIZE);
    return rows.map<ResourcePickerRow>((b) => ({
      id: b.id,
      primary: b.dba ? `${b.name} (${b.dba})` : b.name,
      secondary: [b.status, b.city, b.state].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "brokerage",
        resourceId: b.id,
        displayLabel: `Brokerage — ${b.name}`,
        detailUrl: `/brokerages/${b.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const deal: ResourcePickerAdapter = {
  typeLabel: "Deal",
  placeholder: "Search deals by number or borrower…",
  async search(q) {
    const page = await dealsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((d) => ({
      id: d.id,
      primary: d.businessNameInitial
        ? `${d.dealNumber} — ${d.businessNameInitial}`
        : d.dealNumber,
      secondary: [d.stage, d.subState].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "deal",
        resourceId: d.id,
        displayLabel: d.businessNameInitial
          ? `Deal ${d.dealNumber} — ${d.businessNameInitial}`
          : `Deal ${d.dealNumber}`,
        detailUrl: `/pipeline/${d.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const lead: ResourcePickerAdapter = {
  typeLabel: "Lead",
  placeholder: "Search leads by name or company…",
  async search(q) {
    const page = await leadsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((l) => ({
      id: l.id,
      // A lead is a person at a brokerage — the person's name is primary so
      // contacts at the same firm stay distinguishable (FIND-05/FIND-07);
      // company is fallback only.
      primary: `${l.firstName} ${l.lastName}`.trim() || l.companyName || "",
      secondary: [l.companyName, l.status, l.source, l.state].filter(Boolean).join(" • "),
      toAttachment: () => {
        const name = `${l.firstName} ${l.lastName}`.trim() || l.companyName || "";
        return {
          resourceType: "lead",
          resourceId: l.id,
          displayLabel: `Lead — ${name || "Unnamed"}`,
          detailUrl: `/leads/${l.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        };
      },
    }));
  },
};

const person: ResourcePickerAdapter = {
  typeLabel: "Person",
  placeholder: "Search persons by name…",
  async search(q) {
    const page = await personsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((p) => ({
      id: p.id,
      primary: p.displayName,
      secondary: [p.primaryEmail, p.state, p.status].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "person",
        resourceId: p.id,
        displayLabel: `Person — ${p.displayName}`,
        detailUrl: `/counterparties/persons/${p.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const entity: ResourcePickerAdapter = {
  typeLabel: "Entity",
  placeholder: "Search entities by legal name…",
  async search(q) {
    const page = await entitiesApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((e) => ({
      id: e.id,
      primary: e.dba ? `${e.legalEntityName} (${e.dba})` : e.legalEntityName,
      secondary: [e.entityType, e.stateOfFormation, e.status]
        .filter(Boolean)
        .join(" • "),
      toAttachment: () => ({
        resourceType: "entity",
        resourceId: e.id,
        displayLabel: e.dba
          ? `Entity — ${e.legalEntityName} (${e.dba})`
          : `Entity — ${e.legalEntityName}`,
        detailUrl: `/counterparties/entities/${e.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const business: ResourcePickerAdapter = {
  typeLabel: "Business",
  placeholder: "Search businesses by legal name…",
  async search(q) {
    const page = await businessesApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((b) => ({
      id: b.id,
      primary: b.dba ? `${b.legalEntityName} (${b.dba})` : b.legalEntityName,
      secondary: [b.naicsCode, b.hqState, b.status].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "business",
        resourceId: b.id,
        displayLabel: b.dba
          ? `Business — ${b.legalEntityName} (${b.dba})`
          : `Business — ${b.legalEntityName}`,
        detailUrl: `/counterparties/businesses/${b.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

// ---- Attach-record expansion -----------------------------------------
// Pickers below extend the registry to cover every entity that has a
// Messages tab or a Tasks tab. Two patterns:
//   - Server search: list endpoint accepts a free-text param (q / search).
//   - Browse + client filter: list endpoint has no q param (Tps, the 4
//     service-providers); we fetch a moderate page and filter client-side
//     by the typed query against the resolved name. Acceptable at v1
//     scale; a backend ?q= follow-up tightens this later.
// Joined-name types (Investor, service-providers, Tps) also resolve a
// LegalEntity name map per query since their list DTOs only carry the
// entityId FK.

const vendor: ResourcePickerAdapter = {
  typeLabel: "Vendor",
  placeholder: "Search vendors by name…",
  async search(q) {
    const rows = await vendorsApi.search({ search: q });
    return rows.slice(0, PAGE_SIZE).map<ResourcePickerRow>((v) => ({
      id: v.id,
      primary: v.name,
      secondary: [v.vendorType, v.status].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "vendor",
        resourceId: v.id,
        displayLabel: `Vendor — ${v.name}`,
        detailUrl: `/portfolio-admin/vendors/${v.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const loan: ResourcePickerAdapter = {
  typeLabel: "Loan",
  placeholder: "Search loans by loan number…",
  async search(q) {
    const rows = await loansApi.search({ search: q });
    return rows.slice(0, PAGE_SIZE).map<ResourcePickerRow>((l) => ({
      id: l.id,
      primary: l.loanNumber || "(unnumbered)",
      secondary: [l.accountState, l.creditRating].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "loan",
        resourceId: l.id,
        displayLabel: `Loan — ${l.loanNumber || "(unnumbered)"}`,
        detailUrl: `/portfolio-admin/loans/${l.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const spv: ResourcePickerAdapter = {
  typeLabel: "SPV Trust",
  placeholder: "Search SPV trusts by name…",
  async search(q) {
    const page = await spvTrustsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((s) => ({
      id: s.id,
      primary: s.trustName || "(unnamed)",
      secondary: [s.stateOfFormation, s.formationDate].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "spv",
        resourceId: s.id,
        displayLabel: `SPV Trust: ${s.trustName || "(unnamed)"}`,
        detailUrl: `/capital-markets/trusts/${s.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const spvPool: ResourcePickerAdapter = {
  typeLabel: "SPV Pool",
  placeholder: "Search SPV pools by name…",
  async search(q) {
    const page = await spvPoolsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((p) => ({
      id: p.id,
      primary: p.poolName || "(unnamed)",
      secondary: [p.status, p.offeringType].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "spv_pool",
        resourceId: p.id,
        displayLabel: `SPV Pool: ${p.poolName || "(unnamed)"}`,
        detailUrl: `/capital-markets/pools/${p.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

// Joined-name type — entitiesApi resolves the display name from
// InvestorMaskedDto.personId / entityId. The investor list supports q
// (against the linked person/entity name, server-side).
const investor: ResourcePickerAdapter = {
  typeLabel: "Investor",
  placeholder: "Search investors by name…",
  async search(q) {
    const page = await investorsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    const rows = page.data;
    const personIds = rows.map((r) => r.personId).filter((x): x is string => !!x);
    const entityIds = rows.map((r) => r.entityId).filter((x): x is string => !!x);
    // Resolve names in parallel; tolerate a partial failure by falling back
    // to "(unnamed)".
    const [personNames, entityNames] = await Promise.all([
      personIds.length
        ? personsApi
            .list({ pageSize: 200 })
            .then((p) => new Map(p.data.map((x) => [x.id, x.displayName])))
            .catch(() => new Map<string, string>())
        : Promise.resolve(new Map<string, string>()),
      entityIds.length
        ? entitiesApi
            .list({ pageSize: 200 })
            .then((e) => new Map(e.data.map((x) => [x.id, x.legalEntityName])))
            .catch(() => new Map<string, string>())
        : Promise.resolve(new Map<string, string>()),
    ]);
    return rows.map<ResourcePickerRow>((inv) => {
      const name = inv.personId
        ? personNames.get(inv.personId) ?? "(unnamed)"
        : inv.entityId
          ? entityNames.get(inv.entityId) ?? "(unnamed)"
          : "(unnamed)";
      return {
        id: inv.id,
        primary: name,
        secondary: [inv.investorType, inv.accreditationBasis]
          .filter(Boolean)
          .join(" • "),
        toAttachment: () => ({
          resourceType: "investor",
          resourceId: inv.id,
          displayLabel: `Investor: ${name}`,
          detailUrl: `/capital-markets/investors/${inv.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        }),
      };
    });
  },
};

// Service-provider factory — IndentureTrustee / TrustCompany /
// PlacementAgent / BackupServicer share one shape but four endpoints
// (no shared "type" param). Each picker fetches a moderate browse page,
// resolves entity names from a single entitiesApi roll-up, and filters
// client-side by the typed query.
function buildServiceProviderPicker(args: {
  typeLabel: string;
  resourceType: string;
  detailRouteSegment: string;
  list: (params: { page: number; pageSize: number }) => Promise<{
    data: Array<{ id: string; entityId: string; status: string }>;
  }>;
}): ResourcePickerAdapter {
  return {
    typeLabel: args.typeLabel,
    placeholder: `Search ${args.typeLabel.toLowerCase()}s by name…`,
    async search(q) {
      const [page, entityPage] = await Promise.all([
        args.list({ page: 1, pageSize: 50 }),
        entitiesApi.list({ pageSize: 200 }),
      ]);
      const nameMap = new Map(
        entityPage.data.map((e) => [e.id, e.legalEntityName]),
      );
      const lower = q.toLowerCase();
      const rows = page.data
        .map((p) => ({
          id: p.id,
          name: nameMap.get(p.entityId) ?? "(unknown)",
          status: p.status,
        }))
        .filter((r) => !lower || r.name.toLowerCase().includes(lower))
        .slice(0, PAGE_SIZE);
      return rows.map<ResourcePickerRow>((r) => ({
        id: r.id,
        primary: r.name,
        secondary: r.status || undefined,
        toAttachment: () => ({
          resourceType: args.resourceType,
          resourceId: r.id,
          displayLabel: `${args.typeLabel}: ${r.name}`,
          detailUrl: `/capital-markets/${args.detailRouteSegment}/${r.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        }),
      }));
    },
  };
}

const indentureTrustee = buildServiceProviderPicker({
  typeLabel: "Indenture Trustee",
  resourceType: "indenture_trustee",
  detailRouteSegment: "indenture-trustees",
  list: (p) => indentureTrusteesApi.list(p),
});

const trustCompany = buildServiceProviderPicker({
  typeLabel: "Trust Company",
  resourceType: "trust_company",
  detailRouteSegment: "trust-companies",
  list: (p) => trustCompaniesApi.list(p),
});

const placementAgent = buildServiceProviderPicker({
  typeLabel: "Placement Agent",
  resourceType: "placement_agent",
  detailRouteSegment: "placement-agents",
  list: (p) => placementAgentsApi.list(p),
});

const backupServicer = buildServiceProviderPicker({
  typeLabel: "Backup Servicer",
  resourceType: "backup_servicer",
  detailRouteSegment: "backup-servicers",
  list: (p) => backupServicersApi.list(p),
});

// TPS — same joined-name shape as the service providers, but lives under
// /portfolio-admin/tps and has no q param. Browse + client filter.
const tps: ResourcePickerAdapter = {
  typeLabel: "TPS Provider",
  placeholder: "Search TPS providers by name…",
  async search(q) {
    const [page, entityPage] = await Promise.all([
      tpsApi.list({ page: 1, pageSize: 50 }),
      entitiesApi.list({ pageSize: 200 }),
    ]);
    const nameMap = new Map(
      entityPage.data.map((e) => [e.id, e.legalEntityName]),
    );
    const lower = q.toLowerCase();
    const rows = page.data
      .map((t) => ({
        id: t.id,
        name: nameMap.get(t.entityId) ?? "(unknown)",
        status: t.status as string,
      }))
      .filter((r) => !lower || r.name.toLowerCase().includes(lower))
      .slice(0, PAGE_SIZE);
    return rows.map<ResourcePickerRow>((r) => ({
      id: r.id,
      primary: r.name,
      secondary: r.status || undefined,
      toAttachment: () => ({
        resourceType: "tps",
        resourceId: r.id,
        displayLabel: `TPS: ${r.name}`,
        detailUrl: `/portfolio-admin/tps/${r.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

const localEnforcer: ResourcePickerAdapter = {
  typeLabel: "Local Enforcer",
  placeholder: "Search local enforcers by name or DBA…",
  async search(q) {
    const rows = await localEnforcersApi.search({ search: q });
    return rows.slice(0, PAGE_SIZE).map<ResourcePickerRow>((e) => ({
      id: e.id,
      primary: e.dbaName
        ? `${e.legalEntityName} (${e.dbaName})`
        : e.legalEntityName,
      secondary: [e.enforcerType, e.status].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "local_enforcer",
        resourceId: e.id,
        displayLabel: e.dbaName
          ? `Local Enforcer — ${e.legalEntityName} (${e.dbaName})`
          : `Local Enforcer — ${e.legalEntityName}`,
        detailUrl: `/portfolio-admin/local-enforcers/${e.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

// External Account (PortalUser). The admin registry endpoint is paginated
// and supports a `search` param. The chip routes to the staff admin
// detail page, not the portal route.
const portalUser: ResourcePickerAdapter = {
  typeLabel: "Portal Account",
  placeholder: "Search portal accounts by name or email…",
  async search(q) {
    const response = await portalAccountsApi.list({
      search: q,
      limit: PAGE_SIZE,
      includeArchived: true,
    });
    return response.items.map<ResourcePickerRow>((u) => ({
      id: u.id,
      primary: u.fullName || u.email,
      secondary: [u.email, u.accountType, u.kind].filter(Boolean).join(" • "),
      toAttachment: () => ({
        resourceType: "portal_user",
        resourceId: u.id,
        displayLabel: `Portal Account — ${u.fullName || u.email}`,
        detailUrl: `/admin/portal-accounts/${u.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

// ---- Buyer programme --------------------------------------------------
// Both list endpoints accept `q`, so these are plain server-search adapters.
// Labels mirror ResourceLabelService's chip prefixes ("Listing — …",
// "Submission — …") so the optimistic chip looks the same before the server
// re-resolves it on send.

const listing: ResourcePickerAdapter = {
  typeLabel: "Listing",
  placeholder: "Search listings by business name…",
  async search(q) {
    const page = await listingsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((l) => {
      // The ONE ladder. This picker used to walk businessName -> raw URL and
      // never read `headline` at all, and its own private placeholder differed
      // from every other surface's — so the optimistic chip could name a
      // listing differently from the chip the server re-resolves on send.
      //
      // 🔴 SHORT, in lockstep with ResourceLabelService — which mints the chip
      // that REPLACES this one a beat later. Both a picker row and a chip are
      // one-line slots, and a one-sided cut would put two names on one record
      // a second apart, which is the bug this whole ladder exists to close.
      // Cut INSIDE the label: `Listing — {name}` composes with an em dash, so
      // shortening the finished string would leave every chip reading "Listing".
      const headline = shortenListingName(l.displayName) ?? listingShortName(l);
      return {
        id: l.id,
        primary: headline,
        secondary: [
          [l.city, l.state].filter(Boolean).join(", "),
          l.industry,
          l.marketplaceName,
        ]
          .filter(Boolean)
          .join(" • "),
        toAttachment: () => ({
          resourceType: "listing",
          resourceId: l.id,
          displayLabel: `Listing — ${headline}`,
          detailUrl: `/listings/${l.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        }),
      };
    });
  },
};

const buyerSubmission: ResourcePickerAdapter = {
  typeLabel: "Submission",
  placeholder: "Search submissions by buyer or listing…",
  async search(q) {
    const page = await buyerSubmissionsApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((s) => {
      // The summary DTO carries no rawUrl and no headline, so the ladder cannot
      // be walked here at all — which is why the server resolves it onto the row
      // as `listingDisplayName`. The chip and the label the server re-resolves
      // on send are now the same string rather than merely close.
      //
      // SHORT, in lockstep with ResourceLabelService, and applied INSIDE the
      // pairing: a submission has no name but `{buyer} → {listing}`, so cutting
      // the composed string would cut at the first separator in either half.
      const pairing = `${s.buyerDisplayName} → ${listingShortNameOrFallback(
        s.listingDisplayName,
        s.listingBusinessName,
      )}`;
      return {
        id: s.id,
        primary: pairing,
        secondary: [
          s.status,
          [s.listingCity, s.listingState].filter(Boolean).join(", "),
        ]
          .filter(Boolean)
          .join(" • "),
        toAttachment: () => ({
          resourceType: "buyer_submission",
          resourceId: s.id,
          displayLabel: `Submission — ${pairing}`,
          detailUrl: `/buyer-submissions/${s.id}`,
          isDeleted: false,
          viewerCanAccess: true,
        }),
      };
    });
  },
};

const reunderwritingCase: ResourcePickerAdapter = {
  typeLabel: "Reunderwriting Case",
  placeholder: "Search reunderwriting cases by case number…",
  async search(q) {
    const page = await reunderwritingCasesApi.list({
      q,
      page: 1,
      pageSize: PAGE_SIZE,
    });
    return page.data.map<ResourcePickerRow>((c) => ({
      id: c.id,
      primary: c.caseNumber,
      secondary: [c.eventTypeCode, c.stage, c.subjectLoanNumber]
        .filter(Boolean)
        .join(" • "),
      toAttachment: () => ({
        // Hyphenated message-side token — matches the FE detail page that
        // already passes resourceType="reunderwriting-case" and the
        // backend allow-list, both registered in WS1.
        resourceType: "reunderwriting-case",
        resourceId: c.id,
        displayLabel: `Reunderwriting Case — ${c.caseNumber}`,
        detailUrl: `/reunderwriting/cases/${c.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

/**
 * The marketing-sourced buyer prospect.
 *
 * 🔴 A BUYER LEAD COULD OWN A CONVERSATION WITHOUT BEING ATTACHABLE TO ONE.
 * Its detail page has hosted a Messages tab since the Buyer CRM shipped and
 * `buyer_lead` has been in the server's `ResourceTypes.All` just as long — so
 * sending worked, and only the picker was missing. The asymmetry is the awkward
 * kind: a specialist could stand on a buyer's Messages tab, and could not put
 * that buyer on a message composed anywhere else.
 *
 * 🔴 THE LABEL COMES FROM `displayName`, NOT FROM THE NAME PARTS. The server
 * re-resolves every chip on send through `ResourceLabelService`, whose ladder is
 * `"{first} {last}" → email → phone → "(unnamed)"` — a freshly captured ad lead
 * legitimately has no name yet, and a chip reading "(unnamed)" when an email is
 * on file is useless to whoever is working the queue. `displayName` is that
 * exact ladder already computed server-side (`BuyerLeadService.DisplayNameFor`),
 * so using it keeps the chip and the re-resolved label the same string. Walking
 * the parts again here would be a second copy of the rule, and the copy is what
 * drifts.
 */
const buyerLead: ResourcePickerAdapter = {
  typeLabel: "Buyer Lead",
  placeholder: "Search buyers by name, email or phone…",
  async search(q) {
    const page = await buyersApi.list({ q, page: 1, pageSize: PAGE_SIZE });
    return page.data.map<ResourcePickerRow>((b) => ({
      id: b.id,
      primary: b.displayName,
      // What distinguishes two buyers with the same name: how to reach them,
      // where they are, and how far along they are.
      secondary: [b.email, [b.city, b.state].filter(Boolean).join(", "), b.status]
        .filter(Boolean)
        .join(" • "),
      toAttachment: () => ({
        resourceType: "buyer_lead",
        resourceId: b.id,
        // Byte-identical to ResourceLabelService's own composition.
        displayLabel: `Buyer Lead — ${b.displayName}`,
        detailUrl: `/buyers/${b.id}`,
        isDeleted: false,
        viewerCanAccess: true,
      }),
    }));
  },
};

export const RESOURCE_PICKERS: Record<string, ResourcePickerAdapter> = {
  borrower,
  seller: sellerParty,
  broker,
  brokerage,
  deal,
  lead,
  person,
  entity,
  business,
  vendor,
  loan,
  spv,
  spv_pool: spvPool,
  investor,
  indenture_trustee: indentureTrustee,
  trust_company: trustCompany,
  placement_agent: placementAgent,
  backup_servicer: backupServicer,
  tps,
  local_enforcer: localEnforcer,
  portal_user: portalUser,
  "reunderwriting-case": reunderwritingCase,
  listing,
  buyer_lead: buyerLead,
  buyer_submission: buyerSubmission,
};

/** Resource type keys that have a registered picker. */
export const REGISTERED_PICKER_TYPES = Object.keys(RESOURCE_PICKERS);
